import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ManagerSidebar from '../components/ManagerSidebar';
import ManagerTopHeader from '../components/ManagerTopHeader';

import { API_BASE } from '../../config/runtime';

const getStoredUser = () => {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/* ─── Minimal keyframes Tailwind can't express natively ─────────────────── */
if (typeof document !== 'undefined' && !document.getElementById('mt-keyframes')) {
  const style = document.createElement('style');
  style.id = 'mt-keyframes';
  style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

    .mt-fade-up {
      opacity: 0;
      transform: translateY(16px);
      animation: mtFadeUp 0.4s ease forwards;
    }
    @keyframes mtFadeUp { to { opacity: 1; transform: translateY(0); } }

    .mt-spin { animation: mtSpin 0.8s linear infinite; }
    @keyframes mtSpin { to { transform: rotate(360deg); } }

    @keyframes shimmer {
      0%   { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
    .mt-shimmer {
      background: linear-gradient(90deg,#F1F5F9 25%,#E8EDF2 50%,#F1F5F9 75%);
      background-size: 200% 100%;
      animation: shimmer 1.4s infinite;
      border-radius: 12px;
    }

    @keyframes mtFadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes mtZoom {
      from { opacity: 0; transform: scale(.92); }
      to   { opacity: 1; transform: scale(1); }
    }
    .mt-modal-overlay-anim { animation: mtFadeIn .2s ease; }
    .mt-modal-anim { animation: mtZoom .25s cubic-bezier(.34,1.56,.64,1); }

    .mt-search:focus {
      border-color: #2563EB;
      box-shadow: 0 0 0 4px rgba(37,99,235,.12);
    }
    .mt-search::placeholder { color: #94A3B8; }

    .mt-input:focus {
      border-color: #2563EB;
      box-shadow: 0 0 0 3px rgba(37,99,235,.1);
      background: #fff;
    }
    .mt-input::placeholder { color: #94A3B8; }

    .mt-scrollbar::-webkit-scrollbar { width: 4px; }
    .mt-scrollbar::-webkit-scrollbar-track { background: #F1F5F9; border-radius: 4px; }
    .mt-scrollbar::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 4px; }

    .mt-grid {
      display: grid;
      gap: 16px;
      grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    }
    @media (max-width: 480px) {
      .mt-grid { grid-template-columns: 1fr 1fr; gap: 12px; }
    }

    .mt-clamp-title { font-size: clamp(20px, 3vw, 28px); }
    .mt-clamp-pad { padding: clamp(16px, 4vw, 40px); }
    .mt-clamp-search { width: clamp(200px, 36vw, 360px); }
  `;
  document.head.appendChild(style);
}

/* ─── Icons ───────────────────────────────────────────────────────────────── */
const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M12 5v14M5 12h14"/>
  </svg>
);
const IconSearch = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
  </svg>
);
const IconX = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M18 6 6 18M6 6l12 12"/>
  </svg>
);
const IconDots = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="5" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="19" cy="12" r="1.5"/>
  </svg>
);
const IconUser = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.2" strokeLinecap="round">
    <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
  </svg>
);
const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconWarn = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

/* ─── Therapist Card ──────────────────────────────────────────────────────── */
const TherapistCard = ({ item, onView, onEdit, onDelete, delay = 0 }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div
      className="group mt-fade-up bg-white border border-slate-200 rounded-[20px] overflow-hidden relative transition-all duration-300 hover:shadow-[0_16px_40px_-8px_rgba(15,23,42,0.12)] hover:-translate-y-[3px]"
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Image */}
      <div className="relative h-[180px] bg-slate-50 overflow-hidden">
        {item.image
          ? <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
          : (
            <div className="flex flex-col items-center justify-center h-full gap-2">
              <IconUser />
              <span className="text-[11px] text-slate-300" style={{ fontFamily: 'DM Sans, sans-serif' }}>No photo</span>
            </div>
          )
        }

        {/* ID badge */}
        <div className="absolute top-[10px] left-[10px] bg-slate-900/65 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-[3px] rounded-[6px]" style={{ fontFamily: 'Sora, sans-serif' }}>
          #{item.therapist_id}
        </div>

        {/* Context menu trigger */}
        <div className="absolute top-2 right-2 z-20">
          <button
            onClick={() => setMenuOpen(p => !p)}
            className="w-[30px] h-[30px] rounded-lg bg-white/85 backdrop-blur-sm border border-white/50 flex items-center justify-center cursor-pointer text-slate-500 hover:bg-white transition-colors"
          >
            <IconDots />
          </button>
        </div>

        {/* View button overlay */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20 backdrop-blur-[2px] flex items-center justify-center">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onView?.(item); }}
            className="view-btn bg-white text-black text-[9px] sm:text-[10px] font-black px-4 sm:px-6 py-2 sm:py-2.5 rounded-full shadow-2xl transform translate-y-3 group-hover:translate-y-0 transition-all duration-300 hover:bg-black hover:text-white"
          >
            VIEW
          </button>
        </div>

        {menuOpen && (
          <>
            <div className="fixed inset-0 z-[25]" onClick={() => setMenuOpen(false)} />
            <div className="absolute right-2 top-9 z-30 bg-white border border-slate-200 rounded-xl shadow-[0_8px_24px_rgba(15,23,42,0.12)] overflow-hidden min-w-[120px]">
              <button
                className="block w-full text-left px-[14px] py-[10px] text-xs font-semibold text-slate-700 hover:bg-slate-50 border-none bg-transparent cursor-pointer transition-colors"
                style={{ fontFamily: 'Sora, sans-serif' }}
                onClick={() => { setMenuOpen(false); onEdit(item); }}
              >
                Edit
              </button>
              <button
                className="block w-full text-left px-[14px] py-[10px] text-xs font-semibold text-red-600 hover:bg-red-50 border-none bg-transparent cursor-pointer transition-colors"
                style={{ fontFamily: 'Sora, sans-serif' }}
                onClick={() => { setMenuOpen(false); onDelete(item); }}
              >
                Delete
              </button>
            </div>
          </>
        )}
      </div>

      {/* Body */}
      <div className="px-[14px] pt-[14px] pb-4">
        <p className="font-extrabold text-[13px] text-slate-900 m-0 mb-1.5 leading-snug" style={{ fontFamily: 'Sora, sans-serif' }}>
          {item.name}
        </p>
        <p
          className="text-xs text-slate-500 m-0 leading-relaxed overflow-hidden min-h-[58px]"
          style={{
            fontFamily: 'DM Sans, sans-serif',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {item.bio?.trim() || 'No bio provided.'}
        </p>
      </div>
    </div>
  );
};

/* ─── Skeleton Card ───────────────────────────────────────────────────────── */
const SkeletonCard = () => (
  <div className="bg-white rounded-[20px] overflow-hidden border border-slate-200">
    <div className="mt-shimmer h-[180px]" style={{ borderRadius: 0 }} />
    <div className="p-[14px]">
      <div className="mt-shimmer h-3 mb-2" />
      <div className="mt-shimmer h-3 w-[70%] mb-1.5" />
      <div className="mt-shimmer h-3 w-1/2" />
    </div>
  </div>
);

/* ─── Reusable Alert ──────────────────────────────────────────────────────── */
const Alert = ({ type, message }) => (
  <div className={`flex items-start gap-2.5 rounded-xl px-4 py-3 text-[13px] font-medium border ${type === 'error' ? 'bg-red-50 border-red-200 text-red-600' : 'bg-green-50 border-green-200 text-green-700'}`} style={{ fontFamily: 'DM Sans, sans-serif' }}>
    {type === 'error' ? <IconWarn /> : <IconCheck />}
    <span>{message}</span>
  </div>
);

/* ─── Main Component ──────────────────────────────────────────────────────── */
const ManagerTherapist = () => {
  const user = useMemo(() => getStoredUser(), []);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingTherapistId, setEditingTherapistId] = useState(null);
  const [form, setForm] = useState({ name: '', bio: '' });
  const [therapistImage, setTherapistImage] = useState(null);
  const [therapistImagePreview, setTherapistImagePreview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [therapists, setTherapists] = useState([]);
  const [services, setServices] = useState([]);
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isViewMode, setIsViewMode] = useState(false);

  const fetchTherapists = useCallback(async () => {
    if (!user?.branch_id) return;
    setIsLoadingList(true);
    try {
      const res = await fetch(`${API_BASE}/get_therapists.php?branch_id=${encodeURIComponent(String(user.branch_id))}`);
      const data = await res.json();
      if (data.success) setTherapists(data.therapists || []);
    } catch {}
    finally { setIsLoadingList(false); }
  }, [user?.branch_id]);

  const fetchServices = useCallback(async () => {
    setIsLoadingServices(true);
    try {
      const res = await fetch(`${API_BASE}/get_services.php`);
      const data = await res.json();
      setServices(data.success ? data.services || [] : []);
    } catch { setServices([]); }
    finally { setIsLoadingServices(false); }
  }, []);

  useEffect(() => { fetchTherapists(); }, [fetchTherapists]);
  useEffect(() => { fetchServices(); }, [fetchServices]);

  const resetModal = () => {
    setEditingTherapistId(null);
    setForm({ name: '', bio: '' });
    setTherapistImage(null);
    setTherapistImagePreview('');
    setSelectedServiceIds([]);
    setStatus({ type: '', message: '' });
    setIsViewMode(false);
  };

  const openAddModal = () => { resetModal(); setIsAddModalOpen(true); };
  const closeAddModal = () => { if (isSubmitting) return; setIsAddModalOpen(false); resetModal(); };

  const handleEditTherapist = (therapist) => {
    resetModal();
    setEditingTherapistId(therapist.therapist_id);
    setForm({ name: therapist.name || '', bio: therapist.bio || '' });
    setTherapistImagePreview(therapist.image || '');
    setSelectedServiceIds(
      Array.isArray(therapist.expertise)
        ? therapist.expertise.map(Number).filter(Number.isFinite)
        : []
    );
    setIsAddModalOpen(true);
  };

  const handleViewTherapist = (therapist) => {
    resetModal();
    setForm({ name: therapist.name || '', bio: therapist.bio || '' });
    setTherapistImagePreview(therapist.image || '');
    setSelectedServiceIds(
      Array.isArray(therapist.expertise)
        ? therapist.expertise.map(Number).filter(Number.isFinite)
        : []
    );
    setIsViewMode(true);
    setIsAddModalOpen(true);
  };

  const handleDeleteTherapist = (therapist) => setDeleteTarget(therapist);

  const confirmDeleteTherapist = async () => {
    if (!deleteTarget || !user?.branch_id) return;
    try {
      const res = await fetch(`${API_BASE}/delete_therapist.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ therapist_id: deleteTarget.therapist_id, branch_id: Number(user.branch_id) }),
      });
      const data = await res.json();
      if (!data.success) { setStatus({ type: 'error', message: data.message || 'Failed to delete.' }); return; }
      setStatus({ type: 'success', message: 'Therapist deleted successfully.' });
      setDeleteTarget(null);
      fetchTherapists();
    } catch (err) {
      setStatus({ type: 'error', message: `Delete failed: ${err.message}` });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });
    if (!user?.branch_id) { setStatus({ type: 'error', message: 'No branch found.' }); return; }
    if (!form.name.trim()) { setStatus({ type: 'error', message: 'Name is required.' }); return; }
    setIsSubmitting(true);
    try {
      const payload = new FormData();
      payload.append('branch_id', String(Number(user.branch_id)));
      payload.append('name', form.name.trim());
      payload.append('bio', form.bio.trim());
      payload.append('expertise', JSON.stringify(selectedServiceIds));
      if (editingTherapistId) payload.append('therapist_id', String(editingTherapistId));
      if (therapistImage) payload.append('therapist_image', therapistImage);

      const endpoint = editingTherapistId ? 'update_therapist.php' : 'add_therapist.php';
      const res = await fetch(`${API_BASE}/${endpoint}`, { method: 'POST', body: payload });
      const data = await res.json();

      if (!data.success) {
        setStatus({ type: 'error', message: data.message || 'Failed to save.' });
        setIsSubmitting(false);
        return;
      }
      setStatus({ type: 'success', message: editingTherapistId ? 'Therapist updated.' : `Therapist added. ID #${data.therapist_id ?? 'N/A'}` });
      setIsAddModalOpen(false);
      resetModal();
      fetchTherapists();
    } catch (err) {
      setStatus({ type: 'error', message: `Save failed: ${err.message}` });
    } finally {
      setIsSubmitting(false);
    }
  };

  const groupedServices = useMemo(() =>
    services.reduce((acc, s) => {
      const cat = s.category_name || 'Services';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(s);
      return acc;
    }, {}),
    [services]
  );

  const selectedServiceNames = useMemo(() =>
    services
      .filter((s) => selectedServiceIds.includes(s.id))
      .map((s) => s.name),
    [services, selectedServiceIds]
  );

  const toggleService = (id) =>
    setSelectedServiceIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const filteredTherapists = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return q ? therapists.filter(t =>
      t.name?.toLowerCase().includes(q) || t.bio?.toLowerCase().includes(q)
    ) : therapists;
  }, [therapists, searchTerm]);

  /* ─── Shared button classes ─────────────────────────────────────────────── */
  const btnBase = "inline-flex items-center justify-center gap-1.5 font-bold text-xs tracking-[0.06em] px-5 py-[10px] rounded-xl border-none cursor-pointer transition-all duration-200 active:scale-95";
  const btnPrimary = `${btnBase} bg-blue-600 text-white hover:bg-blue-700 hover:shadow-[0_4px_14px_rgba(37,99,235,0.35)]`;
  const btnGhost = `${btnBase} bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200`;
  const btnDanger = `${btnBase} bg-red-600 text-white hover:bg-red-700`;

  /* ─── Render ───────────────────────────────────────────────────────────── */
  return (
    <div className="flex min-h-screen bg-slate-50" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      <ManagerSidebar />

      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        <ManagerTopHeader pageTitle="Therapists" />

        <div className="flex-1 overflow-y-auto mt-clamp-pad">

          {/* ── Page header ── */}
          <div className="flex flex-wrap items-start justify-between gap-4 mb-7">
            <div>
              <h1 className="mt-clamp-title font-extrabold text-slate-900 m-0" style={{ fontFamily: 'Sora, sans-serif' }}>
                Therapist Management
              </h1>
              <p className="text-slate-500 text-[13px] mt-1">
                {isLoadingList ? '—' : `${therapists.length} therapist${therapists.length !== 1 ? 's' : ''} in your branch`}
              </p>
            </div>
            <button className={btnPrimary} style={{ fontFamily: 'Sora, sans-serif' }} onClick={openAddModal}>
              <IconPlus /> Add Therapist
            </button>
          </div>

          {/* ── Status alert ── */}
          {status.message && (
            <div className="mb-5">
              <Alert type={status.type} message={status.message} />
            </div>
          )}

          {/* ── Toolbar ── */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div className="relative mt-clamp-search">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 flex pointer-events-none">
                <IconSearch />
              </span>
              <input
                className="mt-search w-full py-[11px] pl-[42px] pr-[42px] border-[1.5px] border-slate-200 rounded-xl text-sm font-medium bg-white text-slate-900 outline-none transition-all"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
                placeholder="Search therapists…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-[10px] top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-100 border-none cursor-pointer flex items-center justify-center text-slate-500"
                >
                  <IconX />
                </button>
              )}
            </div>
            <span className="text-xs font-bold text-slate-400 whitespace-nowrap" style={{ fontFamily: 'Sora, sans-serif' }}>
              {filteredTherapists.length} result{filteredTherapists.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* ── Grid ── */}
          {isLoadingList ? (
            <div className="mt-grid">
              {[...Array(5)].map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : filteredTherapists.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 px-5 bg-white rounded-[20px] border border-dashed border-slate-200">
              <IconUser />
              <p className="font-bold text-[15px] text-slate-400 mt-4 mb-1.5" style={{ fontFamily: 'Sora, sans-serif' }}>
                {searchTerm ? 'No therapists match your search' : 'No therapists yet'}
              </p>
              <p className="text-slate-300 text-[13px] mb-5">
                {searchTerm ? 'Try a different name or bio keyword.' : 'Add your first therapist to get started.'}
              </p>
              {!searchTerm && (
                <button className={btnPrimary} style={{ fontFamily: 'Sora, sans-serif' }} onClick={openAddModal}>
                  <IconPlus /> Add Therapist
                </button>
              )}
            </div>
          ) : (
            <div className="mt-grid">
              {filteredTherapists.map((item, idx) => (
                <TherapistCard
                  key={item.therapist_id}
                  item={item}
                  delay={idx * 45}
                  onView={handleViewTherapist}
                  onEdit={handleEditTherapist}
                  onDelete={handleDeleteTherapist}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      {isAddModalOpen && (
        <div
          className="mt-modal-overlay-anim fixed inset-0 z-[200] bg-slate-900/55 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeAddModal}
        >
          <div
            className="mt-modal-anim bg-white rounded-[24px] w-full max-w-[560px] shadow-[0_40px_80px_-12px_rgba(15,23,42,0.3)] max-h-[92vh] flex flex-col overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="px-7 pt-[22px] pb-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="font-extrabold text-lg text-slate-900 m-0" style={{ fontFamily: 'Sora, sans-serif' }}>
                  {isViewMode ? 'View Therapist' : editingTherapistId ? 'Edit Therapist' : 'Add Therapist'}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5 mb-0">
                  {isViewMode ? 'Therapist details are read-only.' : editingTherapistId ? 'Update therapist details' : 'Fill in the details below'}
                </p>
              </div>
              <button
                onClick={closeAddModal}
                className="w-8 h-8 rounded-lg border border-slate-200 bg-slate-50 cursor-pointer flex items-center justify-center text-slate-500 hover:bg-slate-100 transition-colors"
              >
                <IconX />
              </button>
            </div>

            {/* Modal body */}
            <div className="mt-scrollbar overflow-y-auto px-7 py-5 flex-1">
              {isViewMode ? (
                <div className="flex flex-col gap-6">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 mb-2" style={{ fontFamily: 'Sora, sans-serif' }}>
                      Therapist Name
                    </p>
                    <p className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Sora, sans-serif' }}>
                      {form.name || 'N/A'}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 mb-2" style={{ fontFamily: 'Sora, sans-serif' }}>
                      Bio
                    </p>
                    <p className="text-sm text-slate-600 leading-relaxed" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                      {form.bio?.trim() || 'No bio provided.'}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 mb-2" style={{ fontFamily: 'Sora, sans-serif' }}>
                      Areas of Specialization
                    </p>
                    {selectedServiceNames.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {selectedServiceNames.map((name) => (
                          <span key={name} className="text-xs bg-slate-100 text-slate-700 rounded-full px-3 py-2">
                            {name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">No specializations selected.</p>
                    )}
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 mb-2" style={{ fontFamily: 'Sora, sans-serif' }}>
                      Photo
                    </p>
                    {therapistImagePreview ? (
                      <div className="rounded-[20px] overflow-hidden border border-slate-200">
                        <img src={therapistImagePreview} alt={form.name || 'Therapist photo'} className="w-full h-52 object-cover" />
                      </div>
                    ) : (
                      <div className="rounded-[20px] border border-dashed border-slate-300 bg-slate-50 h-52 flex items-center justify-center text-slate-400">
                        No photo available
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-5">

                  {/* Name */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 tracking-[0.04em] mb-1.5" style={{ fontFamily: 'Sora, sans-serif' }}>
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      className="mt-input w-full px-[14px] py-[10px] border-[1.5px] border-slate-200 rounded-[10px] text-sm bg-[#FAFBFC] text-slate-900 outline-none transition-all box-border"
                      style={{ fontFamily: 'DM Sans, sans-serif' }}
                      name="name"
                      value={form.name}
                      onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                      placeholder="Enter therapist full name"
                      maxLength={100}
                      required
                    />
                  </div>

                  {/* Bio */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 tracking-[0.04em] mb-1.5" style={{ fontFamily: 'Sora, sans-serif' }}>
                      Bio <span className="text-slate-400 font-medium">(optional)</span>
                    </label>
                    <textarea
                      className="mt-input w-full px-[14px] py-[10px] border-[1.5px] border-slate-200 rounded-[10px] text-sm bg-[#FAFBFC] text-slate-900 outline-none transition-all box-border resize-y"
                      style={{ fontFamily: 'DM Sans, sans-serif' }}
                      name="bio"
                      value={form.bio}
                      onChange={e => setForm(p => ({ ...p, bio: e.target.value }))}
                      placeholder="Short therapist bio…"
                      rows={3}
                    />
                  </div>

                  {/* Expertise */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-xs font-bold text-slate-700 tracking-[0.04em] m-0" style={{ fontFamily: 'Sora, sans-serif' }}>
                        Areas of Specialization
                      </label>
                      {selectedServiceIds.length > 0 && (
                        <span className="text-[11px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full" style={{ fontFamily: 'Sora, sans-serif' }}>
                          {selectedServiceIds.length} selected
                        </span>
                      )}
                    </div>
                    <div className="bg-slate-50 border-[1.5px] border-slate-200 rounded-[14px] px-4 py-[14px]">
                      {isLoadingServices ? (
                        <p className="text-[13px] text-slate-400 m-0">Loading services…</p>
                      ) : services.length === 0 ? (
                        <p className="text-[13px] text-slate-400 m-0">No services available.</p>
                      ) : (
                        <div className="flex flex-col gap-4">
                          {Object.entries(groupedServices).map(([cat, items]) => (
                            <div key={cat}>
                              <div className="flex items-center gap-3 mt-1 mb-3">
                                <div className="flex-1 h-px bg-slate-200" />
                                <span className="text-[10px] font-extrabold tracking-[0.12em] uppercase text-slate-400 whitespace-nowrap" style={{ fontFamily: 'Sora, sans-serif' }}>
                                  {cat}
                                </span>
                                <div className="flex-1 h-px bg-slate-200" />
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {items.map(s => (
                                  <button
                                    key={s.id}
                                    type="button"
                                    disabled
                                    className={`text-xs font-semibold px-[13px] py-[5px] rounded-full border-[1.5px] ${selectedServiceIds.includes(s.id) ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-200 text-slate-500'}`}
                                    style={{ fontFamily: 'DM Sans, sans-serif' }}
                                  >
                                    {s.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Image upload */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 tracking-[0.04em] mb-1.5" style={{ fontFamily: 'Sora, sans-serif' }}>
                      Photo <span className="text-slate-400 font-medium">(optional)</span>
                    </label>
                    <div className="border-[1.5px] border-dashed border-slate-300 rounded-[14px] p-4 bg-slate-50 transition-colors">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        onChange={e => {
                          const file = e.target.files?.[0] || null;
                          setTherapistImage(file);
                          if (file) setTherapistImagePreview(URL.createObjectURL(file));
                        }}
                        className="w-full text-[13px] text-slate-500"
                        style={{ fontFamily: 'DM Sans, sans-serif' }}
                      />
                      <p className="text-[11px] text-slate-400 mt-2 mb-0">JPG, PNG, WEBP — max 2 MB</p>
                    </div>
                    {therapistImagePreview && (
                      <div className="mt-3 relative rounded-xl overflow-hidden h-[160px]">
                        <img src={therapistImagePreview} alt="Preview" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
                      </div>
                    )}
                  </div>

                  {/* Form status */}
                  {status.message && <Alert type={status.type} message={status.message} />}

                  {/* Actions */}
                  <div className="flex justify-end gap-2.5 mt-6 pt-5 border-t border-slate-100">
                    <button
                      type="button"
                      className={btnGhost}
                      style={{ fontFamily: 'Sora, sans-serif' }}
                      onClick={closeAddModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`${btnPrimary} min-w-[140px]`}
                      style={{ fontFamily: 'Sora, sans-serif', opacity: isSubmitting ? 0.6 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                    >
                      {isSubmitting
                        ? <>
                            <div className="mt-spin w-[14px] h-[14px] border-2 border-white/40 border-t-white rounded-full" />
                            Saving…
                          </>
                        : editingTherapistId ? 'Update Therapist' : 'Add Therapist'
                      }
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleteTarget && (
        <div
          className="mt-modal-overlay-anim fixed inset-0 z-[200] bg-slate-900/55 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setDeleteTarget(null)}
        >
          <div
            className="mt-modal-anim bg-white rounded-[24px] w-full max-w-[420px] shadow-[0_40px_80px_-12px_rgba(15,23,42,0.3)]"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-7 pb-6">
              {/* Icon */}
              <div className="w-12 h-12 rounded-[14px] bg-red-50 flex items-center justify-center mb-4 text-red-600">
                <IconWarn />
              </div>
              <h3 className="font-extrabold text-[17px] text-slate-900 m-0 mb-2" style={{ fontFamily: 'Sora, sans-serif' }}>
                Delete Therapist?
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed m-0">
                Are you sure you want to remove{' '}
                <strong className="text-slate-900">"{deleteTarget.name}"</strong>?
                {' '}This action cannot be undone.
              </p>

              {status.message && status.type === 'error' && (
                <div className="mt-[14px]">
                  <Alert type="error" message={status.message} />
                </div>
              )}

              <div className="flex justify-end gap-2.5 mt-6">
                <button
                  className={btnGhost}
                  style={{ fontFamily: 'Sora, sans-serif' }}
                  onClick={() => setDeleteTarget(null)}
                >
                  Cancel
                </button>
                <button
                  className={btnDanger}
                  style={{ fontFamily: 'Sora, sans-serif' }}
                  onClick={confirmDeleteTherapist}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerTherapist;
